import React, { useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";

export default function MapPicker({ lat, lng, onChange, height = 260, zoom = 15, apiKey }) {
  const containerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    const key = apiKey || import.meta.env.VITE_GOOGLE_MAPS_API_KEY || (typeof window !== 'undefined' ? (window.PPAC_MAPS_KEY || '') : '');
    if (!key) return; // no key; parent should hide this component

    let cancelled = false;
    let mapInstance;

    const init = (google) => {
      if (cancelled || !containerRef.current) return;
      const center = { lat: Number(lat || 14.5995), lng: Number(lng || 120.9842) }; // Manila default
      mapInstance = new google.maps.Map(containerRef.current, {
        center,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      // Prefer AdvancedMarkerElement when available
      let mk;
      if (google.maps.marker?.AdvancedMarkerElement) {
        mk = new google.maps.marker.AdvancedMarkerElement({ map: mapInstance, position: center });
      } else {
        mk = new google.maps.Marker({ position: center, map: mapInstance, draggable: false });
      }
      setMarker(mk);
      setMap(mapInstance);

      mapInstance.addListener("click", (e) => {
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        if (mk.position && typeof mk.position === 'function') {
          // AdvancedMarkerElement uses position property, not setPosition
          mk.position = pos;
        } else if (mk.setPosition) {
          mk.setPosition(pos);
        }
        onChange?.(pos.lat, pos.lng);
      });
    };

    if (window.google?.maps) {
      init(window.google);
    } else {
      const loader = new Loader({ apiKey: key, version: "weekly", libraries: [], id: 'ppac-gmaps-loader', retries: 2, params: { loading: 'async' } });
      loader.load().then((google) => init(google)).catch(() => {});
    }

    return () => {
      cancelled = true;
      if (marker) {
        if (marker.setMap) marker.setMap(null);
        // AdvancedMarkerElement cleanup is GC'd when map is dropped
      }
    };
  }, [apiKey, height, zoom]);

  // When lat/lng props change externally, update marker/map center
  useEffect(() => {
    if (!map || !marker) return;
    const pos = { lat: Number(lat), lng: Number(lng) };
    if (isFinite(pos.lat) && isFinite(pos.lng)) {
      marker.setPosition(pos);
      map.setCenter(pos);
    }
  }, [lat, lng, map, marker]);

  return (
    <div className="relative">
      <div ref={containerRef} style={{ width: "100%", height }} />
      <div className="absolute top-2 right-2">
        <button
          type="button"
          className="btn btn-xs btn-outline"
          onClick={() => {
            if (!navigator.geolocation || !map || !marker) return;
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                if (marker.position && typeof marker.position === 'function') {
                  marker.position = p;
                } else if (marker.setPosition) {
                  marker.setPosition(p);
                }
                map.setCenter(p);
                onChange?.(p.lat, p.lng);
              },
              () => {}
            );
          }}
        >Locate me</button>
      </div>
    </div>
  );
}
